/**
 * The Payment requests screen: the tabbed table, the detail drawer and the
 * New request form's derived figures.
 *
 * Pure, so the grouping, the counts and the totals can be tested without
 * rendering. Spec: DESIGN-BRIEF.md §E and `vRequests()`, `vReqDrawer()` and
 * `vNewReq()` in docs/design/netly-pay/prototype.html.
 *
 * Nothing here adds up money on its own: totals, balances and statuses all
 * come from NetlyPayManager, which is the arithmetic the API uses.
 */
import {
  batchOf,
  diffDays,
  fmtDate,
  fmtDateTime,
  money,
  recipientsFor,
  requestBalance,
  requestPaid,
  requestStatus,
  requestTotal,
  todayISO,
  type Member,
  type NetlyPayData,
  type PaymentRequest,
  type RequestStatus,
} from '../../internals/managers/NetlyPayManager';
import {initialsOf} from './members';

/** The tab a request sits under. Partly paid belongs with Due, per §E. */
export type RequestTab = 'due' | 'overdue' | 'waiting' | 'paid' | 'draft';

export const REQUEST_TABS: {key: RequestTab; label: string}[] = [
  {key: 'due', label: 'Due'},
  {key: 'overdue', label: 'Overdue'},
  {key: 'waiting', label: 'Waiting for approval'},
  {key: 'paid', label: 'Paid'},
  {key: 'draft', label: 'Drafts'},
];

/**
 * Which tab a request shows under.
 *
 * Partly paid folds into Due rather than Overdue even when the due date has
 * passed: `requestStatus` returns 'partly' before it looks at the date, so
 * someone who has paid half is not swept up by the overdue reminder run. That
 * is the one case where the tab and the chip differ — the row still reads
 * "Partly paid".
 */
export function requestTab(r: PaymentRequest, today: string = todayISO()): RequestTab | 'cancelled' {
  const status = requestStatus(r, today);
  if (status === 'partly') return 'due';
  if (status === 'cancelled') return 'cancelled';
  return status;
}

export interface RequestFilters {
  tab: RequestTab;
  q: string;
  batchId: string;
}

export const NO_REQUEST_FILTERS: RequestFilters = {tab: 'due', q: '', batchId: 'all'};

export interface RequestRow {
  id: string;
  title: string;
  /** " · monthly" for a recurring request, else blank. */
  repeat: string;
  who: string;
  whoSub: string;
  initials: string;
  issued: string;
  due: string;
  /** "3 days late" or "In 2 days" — blank when neither applies. */
  dueNote: string;
  dueTone: 'default' | 'warning' | 'danger';
  status: RequestStatus;
  amount: string;
  balance: string;
  balanceTone: 'default' | 'danger' | 'muted';
  canRemind: boolean;
  canReview: boolean;
  isDraft: boolean;
  isPaid: boolean;
  /** The proof to open when Review is clicked, if there is one. */
  approvalId: string | null;
}

/** "Batch U15", "Football", or "3 members" — who a group request addresses. */
function recipientLabel(data: NetlyPayData, r: PaymentRequest): string {
  const rec = r.recipients;
  if (!rec) return '—';
  if (rec.mode === 'batch') {
    const names = rec.ids.map((id) => batchOf(data, id)?.name ?? id);
    return `Batch ${names.join(', ')}`;
  }
  if (rec.mode === 'program') {
    return rec.ids.map((id) => (data.programs ?? []).find((p) => p.id === id)?.name ?? id).join(', ');
  }
  return `${rec.ids.length} ${rec.ids.length === 1 ? 'member' : 'members'}`;
}

/**
 * How the due date reads on the row.
 *
 * Only the next five days get a countdown — "In 23 days" on every request
 * would be noise, and the date column already says when. The day itself reads
 * "Due today" rather than the prototype's "In 0 days".
 */
export function dueNote(
  r: PaymentRequest,
  today: string = todayISO(),
): {note: string; tone: 'default' | 'warning' | 'danger'} {
  const status = requestStatus(r, today);
  const late = diffDays(r.due, today);
  if (status === 'overdue' || (status === 'waiting' && late > 0)) {
    return {note: `${late} ${late === 1 ? 'day' : 'days'} late`, tone: 'danger'};
  }
  if (status === 'due' && late === 0) return {note: 'Due today', tone: 'warning'};
  if (status === 'due' && late < 0 && late >= -5) {
    const days = -late;
    return {note: `In ${days} ${days === 1 ? 'day' : 'days'}`, tone: 'warning'};
  }
  return {note: '', tone: 'default'};
}

export interface RequestTable {
  rows: RequestRow[];
  /** Counts per tab, over every request, not the filtered set. */
  counts: Record<string, number>;
  summary: string;
}

export function requestTable(
  data: NetlyPayData,
  filters: RequestFilters = NO_REQUEST_FILTERS,
  today: string = todayISO(),
  currency = 'Rs.',
): RequestTable {
  const members = data?.members ?? [];
  const approvals = data?.approvals ?? [];
  // A cancelled request is gone from the owner's working view. It is not
  // deleted — the member's timeline still shows it — but it belongs under no
  // tab, so it is left out of the counts too.
  const visible = (data?.requests ?? []).filter((r) => r.state !== 'cancelled');

  const counts: Record<string, number> = {};
  visible.forEach((r) => {
    const tab = requestTab(r, today);
    counts[tab] = (counts[tab] ?? 0) + 1;
  });

  const needle = filters.q.trim().toLowerCase();

  const rows = visible
    .filter((r) => requestTab(r, today) === filters.tab)
    .filter((r) => {
      const member = r.memberId ? members.find((m) => m.id === r.memberId) : null;
      if (filters.batchId !== 'all') {
        if (member) {
          if (member.batchId !== filters.batchId) return false;
        } else if (!r.recipients?.ids.includes(filters.batchId)) {
          return false;
        }
      }
      if (!needle) return true;
      return (
        r.id.toLowerCase().includes(needle) ||
        r.title.toLowerCase().includes(needle) ||
        !!member?.name.toLowerCase().includes(needle)
      );
    })
    // Paid requests read newest first — the last thing collected is what an
    // owner is looking for. Everything else reads by what is owed soonest.
    .sort((a, b) =>
      filters.tab === 'paid' ? b.issued.localeCompare(a.issued) : a.due.localeCompare(b.due),
    )
    .map((r) => {
      const member = r.memberId ? members.find((m) => m.id === r.memberId) : null;
      const status = requestStatus(r, today);
      const balance = requestBalance(r);
      const {note, tone} = dueNote(r, today);
      const approval = approvals.find((a) => a.requestId === r.id);
      const reach = !member && r.recipients ? recipientsFor(data, r.recipients.mode, r.recipients.ids) : [];

      return {
        id: r.id,
        title: r.title,
        repeat: r.recurring ? ' · monthly' : '',
        who: member ? member.name : recipientLabel(data, r),
        whoSub: member
          ? `${member.id} · ${batchOf(data, member.batchId)?.name ?? '—'}`
          : `${reach.length} ${reach.length === 1 ? 'recipient' : 'recipients'}`,
        initials: member ? initialsOf(member.name) : '⋯',
        issued: fmtDate(r.issued),
        due: fmtDate(r.due),
        dueNote: note,
        dueTone: tone,
        status,
        amount: money(requestTotal(r), currency),
        balance: status === 'draft' ? '—' : money(balance, currency),
        balanceTone:
          balance > 0 && status !== 'draft' ? (status === 'overdue' ? 'danger' : 'default') : 'muted',
        canRemind: status === 'due' || status === 'overdue' || status === 'partly',
        canReview: status === 'waiting' && !!approval,
        isDraft: status === 'draft',
        isPaid: status === 'paid',
        approvalId: approval?.id ?? null,
      } as RequestRow;
    });

  const owedIn = (statuses: RequestStatus[]) =>
    visible
      .filter((r) => statuses.includes(requestStatus(r, today)))
      .reduce((s, r) => s + requestBalance(r), 0);

  const summary = `${money(owedIn(['due', 'partly']), currency)} due · ${money(
    owedIn(['overdue']),
    currency,
  )} overdue`;

  return {rows, counts, summary};
}

/** The empty-state copy per tab, from §E. */
export const REQUEST_EMPTY: Record<RequestTab, {title: string; text: string}> = {
  due: {title: 'Nothing due', text: 'Requests you send appear here until they are paid.'},
  overdue: {
    title: 'Nothing overdue',
    text: 'Requests past their due date appear here, with reminders.',
  },
  waiting: {
    title: 'No proofs waiting',
    text: 'When a member uploads a bank slip or LankaQR receipt, the request moves here.',
  },
  paid: {title: 'No paid requests yet', text: 'Approved and cash payments appear here with receipts.'},
  draft: {title: 'No drafts', text: 'Save a request as a draft to finish it later.'},
};

// ── The detail drawer ───────────────────────────────────────────────────────

export type TimelineTone = 'done' | 'ok' | 'warn' | 'accent' | 'bad' | 'next';

export interface TimelineEntry {
  at: string;
  when: string;
  text: string;
  tone: TimelineTone;
}

export interface RequestDetail {
  id: string;
  title: string;
  status: RequestStatus;
  who: string;
  sums: {label: string; value: string; tone: 'default' | 'pos' | 'danger'}[];
  items: {label: string; amount: string; isCredit: boolean}[];
  total: string;
  /** "Repeats monthly · Late fee Rs. 500.00 after 7 days · Part payment allowed" */
  terms: string;
  timeline: TimelineEntry[];
  payments: {date: string; method: string; ref: string; amount: string}[];
  canRemind: boolean;
  canReview: boolean;
  canCredit: boolean;
  canCancel: boolean;
  /** A credit note may not exceed what is still owed. */
  creditMaxMinor: number;
  approvalId: string | null;
}

/**
 * Everything the 560px request drawer shows.
 *
 * The timeline is assembled from what the API actually reports — created,
 * sent, reminders, proofs, rejections, payments, credit notes, cancellation —
 * and then sorted. The prototype also showed a hard-coded "Viewed by" entry a
 * day after issue; nothing records that, so it is left out rather than
 * invented.
 */
export function requestDetail(
  data: NetlyPayData,
  r: PaymentRequest,
  today: string = todayISO(),
  currency = 'Rs.',
): RequestDetail {
  const member = (data?.members ?? []).find((m) => m.id === r.memberId);
  const status = requestStatus(r, today);
  const total = requestTotal(r);
  const paid = requestPaid(r);
  const balance = requestBalance(r);
  const approval = (data?.approvals ?? []).find((a) => a.requestId === r.id);

  const timeline: TimelineEntry[] = [];
  const add = (at: string, text: string, tone: TimelineTone) => timeline.push({at, text, tone, when: ''});

  add(r.issued, `Created by ${r.createdBy ?? 'staff'}`, 'done');
  if (r.state !== 'draft') add(r.issued, "Sent to the member's app", 'done');
  (r.reminders ?? []).forEach((at) => add(at, 'Reminder sent', 'warn'));
  (data?.approvals ?? [])
    .filter((a) => a.requestId === r.id)
    .forEach((a) => add(a.submitted, `Proof submitted · ${money(a.amountMinor, currency)} by ${a.method}`, 'accent'));
  (r.rejections ?? []).forEach((x) => add(x.at, `Proof rejected · ${x.reason}`, 'bad'));
  (r.payments ?? []).forEach((p) => add(p.date, `Paid ${money(p.amountMinor, currency)} · ${p.method}`, 'ok'));
  if (r.creditMinor) {
    add(
      r.creditAt ?? today,
      `Credit note ${money(r.creditMinor, currency)}${r.creditReason ? ` · ${r.creditReason}` : ''}`,
      'accent',
    );
  }
  if (status === 'cancelled') add(r.cancelledAt ?? today, 'Cancelled', 'bad');

  timeline.sort((a, b) => a.at.localeCompare(b.at));
  // The last entry looks forward, so it is appended after the sort: an
  // upcoming due date has no timestamp to sort by.
  if (status === 'due' || status === 'partly') add(r.due, `Due ${fmtDate(r.due)}`, 'next');
  if (status === 'overdue') add(r.due, `Overdue since ${fmtDate(r.due)}`, 'bad');

  const items = (r.items ?? []).map((i) => ({
    label: i.label,
    amount: money(i.amountMinor, currency),
    isCredit: false,
  }));
  if (r.creditMinor) {
    items.push({label: 'Credit note', amount: `−${money(r.creditMinor, currency)}`, isCredit: true});
  }

  const terms = [
    r.recurring ? 'Repeats monthly' : 'One-off',
    r.lateFeeMinor
      ? `Late fee ${money(r.lateFeeMinor, currency)}${
          r.lateFeeDays ? ` after ${r.lateFeeDays} ${r.lateFeeDays === 1 ? 'day' : 'days'}` : ''
        }`
      : null,
    r.partAllowed ? 'Part payment allowed' : 'Full payment only',
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    id: r.id,
    title: r.title,
    status,
    who: member ? `${member.name} · ${member.id}` : recipientLabel(data, r),
    sums: [
      {label: 'AMOUNT', value: money(total, currency), tone: 'default'},
      {label: 'PAID', value: money(paid, currency), tone: paid ? 'pos' : 'default'},
      {
        label: 'BALANCE',
        value: money(balance, currency),
        tone: balance > 0 && status === 'overdue' ? 'danger' : 'default',
      },
    ],
    items,
    total: money(total, currency),
    terms,
    timeline: timeline.map((e) => ({
      ...e,
      when: e.tone === 'next' ? 'Upcoming' : fmtDateTime(e.at),
    })),
    payments: (r.payments ?? []).map((p) => ({
      date: fmtDate(p.date),
      method: p.method,
      ref: p.ref ?? '',
      amount: money(p.amountMinor, currency),
    })),
    canRemind: status === 'due' || status === 'overdue' || status === 'partly',
    canReview: status === 'waiting' && !!approval,
    canCredit: balance > 0 && status !== 'cancelled',
    canCancel: status !== 'paid' && status !== 'cancelled',
    creditMaxMinor: balance,
    approvalId: approval?.id ?? null,
  };
}

/**
 * Is this credit note allowed?
 *
 * Above zero and no more than the balance. A credit note for more than is
 * owed would make the request read as paid while the member still owes
 * nothing — and would hide a refund that belongs in the ledger instead.
 */
export function creditNoteError(amountMinor: number, maxMinor: number, currency = 'Rs.'): string | null {
  if (!Number.isFinite(amountMinor) || amountMinor <= 0) return 'Enter an amount above 0.';
  if (amountMinor > maxMinor) return `The balance is ${money(maxMinor, currency)}. A credit note cannot be more.`;
  return null;
}

// ── The New request drawer ──────────────────────────────────────────────────

export interface NewRequestForm {
  mode: 'member' | 'batch' | 'program';
  ids: string[];
  title: string;
  /** The first line is the recipient's own batch fee, which differs per member. */
  useBatchFee: boolean;
  items: {label: string; amount: string}[];
  due: string;
  repeat: boolean;
  repeatDay: string;
  endsAfter: string;
  late: boolean;
  lateAmount: string;
  lateDays: string;
  part: boolean;
  partMin: string;
  note: string;
  draftId: string | null;
}

export function newRequestForm(patch: Partial<NewRequestForm> = {}): NewRequestForm {
  return {
    mode: 'batch',
    ids: [],
    title: '',
    useBatchFee: true,
    items: [],
    due: '',
    repeat: true,
    repeatDay: '25',
    endsAfter: '',
    late: true,
    lateAmount: '500',
    lateDays: '7',
    part: false,
    partMin: '2000',
    note: '',
    draftId: null,
    ...patch,
  };
}

export function newRequestErrors(data: NetlyPayData, f: NewRequestForm): string[] {
  const errors: string[] = [];
  if (!recipientsFor(data, f.mode, f.ids).length) errors.push('Choose at least one recipient.');
  if (!f.title.trim()) errors.push('Add a title.');
  if (!f.useBatchFee && !f.items.length) errors.push('Add at least one line item.');
  if (f.items.some((i) => !(Number(i.amount) > 0))) errors.push('Every line item needs an amount above 0.');
  if (!f.due) errors.push('Set a due date.');
  return errors;
}

/**
 * The lines one recipient would receive, in minor units.
 *
 * The batch fee line is per member, which is why the total cannot be "fee ×
 * recipients": a request to a program spanning U10 and U15 bills each at
 * their own fee.
 */
export function itemsFor(
  data: NetlyPayData,
  f: NewRequestForm,
  member?: Member | null,
): {label: string; amountMinor: number}[] {
  const items: {label: string; amountMinor: number}[] = [];
  if (f.useBatchFee && member) {
    const batch = batchOf(data, member.batchId);
    items.push({label: `Monthly fee · ${batch?.name ?? 'batch'}`, amountMinor: batch?.feeMinor ?? 0});
  }
  f.items.forEach((i) =>
    items.push({label: i.label.trim() || 'Item', amountMinor: Math.round(Number(i.amount) * 100) || 0}),
  );
  return items;
}

export interface NewRequestSummary {
  recipients: Member[];
  /** What the footer button says — the count must match `recipientsFor`. */
  sendLabel: string;
  recipientLine: string;
  footLine: string;
  totalMinor: number;
  /** The distinct batch fees among the recipients, for the "use batch fee" row. */
  feeRange: string;
  preview: {
    greeting: string;
    title: string;
    items: {label: string; amount: string}[];
    total: string;
    due: string;
    notes: string[];
  };
}

/**
 * The live figures the drawer shows while it is being filled in.
 *
 * `recipients` is `recipientsFor`, which already drops frozen, expired and
 * pending members — so "Send to 7 members" is what will actually be sent, not
 * the size of the roster. A batch of eight with one frozen player reads 7.
 */
export function newRequestSummary(
  data: NetlyPayData,
  f: NewRequestForm,
  currency = 'Rs.',
): NewRequestSummary {
  const recipients = recipientsFor(data, f.mode, f.ids);
  const extraMinor = f.items.reduce((s, i) => s + (Math.round(Number(i.amount) * 100) || 0), 0);
  const totalMinor = recipients.reduce(
    (s, m) => s + (f.useBatchFee ? batchOf(data, m.batchId)?.feeMinor ?? 0 : 0) + extraMinor,
    0,
  );

  const fees = Array.from(new Set(recipients.map((m) => batchOf(data, m.batchId)?.feeMinor ?? 0))).sort(
    (a, b) => a - b,
  );

  const first = recipients[0] ?? null;
  const previewItems = itemsFor(data, f, first);
  const previewTotal = previewItems.reduce((s, i) => s + i.amountMinor, 0);
  const guardian = first?.guardians?.[0];
  const who = guardian ? guardian.name.split(' ')[0] : first?.name.split(' ')[0];

  const notes: string[] = [];
  if (f.late) {
    notes.push(
      `A late fee of ${money(Math.round(Number(f.lateAmount) * 100) || 0, currency)} is added ${
        Number(f.lateDays) || 0
      } days after the due date.`,
    );
  }
  if (f.part) {
    notes.push(
      `You can pay in parts. Minimum ${money(Math.round(Number(f.partMin) * 100) || 0, currency)}.`,
    );
  }
  if (f.repeat) notes.push(`Repeats every month on day ${f.repeatDay || '—'}.`);
  if (f.note.trim()) notes.push(f.note.trim());

  const n = recipients.length;
  return {
    recipients,
    sendLabel: n ? `Send to ${n} ${n === 1 ? 'member' : 'members'}` : 'Send',
    recipientLine: n
      ? `${n} ${n === 1 ? 'recipient' : 'recipients'} · ${money(totalMinor, currency)} in total`
      : 'No recipients yet',
    footLine: n
      ? `${money(totalMinor, currency)} across ${n} ${n === 1 ? 'request' : 'requests'}`
      : 'Choose who receives this request.',
    totalMinor,
    feeRange: fees.length ? fees.map((x) => money(x, currency)).join(' · ') : 'Pick recipients',
    preview: {
      greeting: `Hi ${who ?? 'there'}, a new request for ${first ? first.name.split(' ')[0] : 'your member'}.`,
      title: f.title.trim() || 'Untitled request',
      items: previewItems.map((i) => ({label: i.label, amount: money(i.amountMinor, currency)})),
      total: money(previewTotal, currency),
      due: fmtDate(f.due),
      notes,
    },
  };
}

/** The body the API takes — one request per recipient (CONTRACT-NETLISE §2). */
export function newRequestBody(data: NetlyPayData, f: NewRequestForm, asDraft: boolean) {
  return {
    draft: asDraft,
    recipients: {mode: f.mode, ids: f.ids},
    title: f.title.trim(),
    useBatchFee: f.useBatchFee,
    items: f.items
      .filter((i) => Number(i.amount) > 0)
      .map((i) => ({label: i.label.trim() || 'Item', amountMinor: Math.round(Number(i.amount) * 100)})),
    due: f.due,
    recurring: f.repeat,
    repeatDay: f.repeat ? Number(f.repeatDay) || null : null,
    endsAfter: f.repeat && f.endsAfter ? Number(f.endsAfter) : null,
    lateFeeMinor: f.late ? Math.round(Number(f.lateAmount) * 100) || 0 : null,
    lateFeeDays: f.late ? Number(f.lateDays) || 0 : null,
    partAllowed: f.part,
    partMinMinor: f.part ? Math.round(Number(f.partMin) * 100) || 0 : null,
    note: f.note.trim() || undefined,
    replacesDraftId: f.draftId ?? undefined,
  };
}

/** Reopen a saved draft in the form it was written in. */
export function formFromDraft(r: PaymentRequest): NewRequestForm {
  const major = (minor?: number | null) => (minor == null ? '' : String(minor / 100));
  return newRequestForm({
    mode: r.recipients?.mode ?? 'member',
    ids: r.recipients?.ids.slice() ?? (r.memberId ? [r.memberId] : []),
    title: r.title,
    // A draft stores the lines it was saved with, so the per-member batch fee
    // is no longer implied — everything is an explicit item.
    useBatchFee: false,
    items: (r.items ?? []).map((i) => ({label: i.label, amount: String(i.amountMinor / 100)})),
    due: r.due,
    repeat: !!r.recurring,
    late: !!r.lateFeeMinor,
    lateAmount: r.lateFeeMinor ? major(r.lateFeeMinor) : '500',
    lateDays: r.lateFeeDays ? String(r.lateFeeDays) : '7',
    part: !!r.partAllowed,
    partMin: r.partMinMinor ? major(r.partMinMinor) : '2000',
    note: r.note ?? '',
    draftId: r.id,
  });
}
